import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import CourseTree from "../../components/CourseTree";
import AddRelationsForm from "../../components/CourseTree/CourseTreeForm";
import { Input } from "../../components/Form/Input";

import RelationsService from "../../services/relations";
import RelationTreeService from "./service";
import { filterRelations, getExpandedNodeIds } from "./searchUtils";
import { filterTreeByFormSelection } from "./treeFilter";
import type {
  Role as TreeRole,
  Aircraft as TreeAircraft,
  Position as TreePosition,
  Operation as TreeOperation,
  Course,
} from "./searchUtils";
import useDebounce from "../../hooks/useDebounce";
import useCrew from "../../hooks/useCrew";
import {
  createCourseRelation,
  deleteRelation,
  toggleCourseVisibility,
  getAvailableCourses,
  getRelationTree,
} from "../../api/courses";
import type { CourseVisibilityToggle, CourseRelationInput } from "../../api/courses";
import type { RelationTreeResponse } from "../../shared/api-types/course";
import { getRoles, getRolesByCrewId } from "../../api/roles";
import type { Role as CrewRole } from "../../api/roles";
import { getCrewAircraftTypes } from "../../api/aircraft";
import { CourseRelationsContextProvider } from "../../context/CourseRelationsContext";

/** A dropdown entry that can be a designator or a variant type */
interface AircraftDropdownItem {
  id: number;
  kind: "designator" | "type";
  name: string;
  designatorId?: number | null;
}

interface SelectedFormValues {
  roles?: TreeRole[];
  position?: TreePosition;
  operations?: TreeOperation[];
  aircraft?: AircraftDropdownItem[];
}

interface FormValues {
  roles: TreeRole[];
  courses: Course[];
}

const buildFormRoles = (roles: CrewRole[], relations: RelationTreeResponse["relations"]): TreeRole[] => {
  const operationsById = new Map(relations.operations.map((operation) => [operation.id, operation]));
  const positionsByRoleId = new Map<number, TreePosition[]>();

  relations.positions.forEach((position) => {
    const positions = positionsByRoleId.get(position.roleId) ?? [];
    positions.push({
      ...position,
      operations: position.operations
        .map((operationId) => operationsById.get(operationId))
        .filter((operation): operation is TreeOperation => Boolean(operation)),
    });
    positionsByRoleId.set(position.roleId, positions);
  });

  return roles.map((role) => ({
    ...role,
    positions: positionsByRoleId.get(role.id) ?? [],
    courses: [],
    customCourses: [],
    groups: [],
    aircraft: [],
  }));
};

const RelationsTreeFeature = () => {
  const formRef = useRef<HTMLDivElement>(null);
  const { crew } = useCrew() as { crew?: { id?: number | string } };
  const currentCrewId = Number(crew?.id);
  const crewId: number | null = currentCrewId ? currentCrewId : null;
  const isSuperAdmin = !crewId;
  const [refresh, setRefresh] = useState(0);
  // Two server-tree variants: without and with hidden relations. The variant
  // matching the hidden toggle is fetched on load; the other lazily on demand.
  // Both reset on refresh/crew change so mutations invalidate them together.
  const [visibleTree, setVisibleTree] = useState<RelationTreeResponse | null>(null);
  const [mergedTree, setMergedTree] = useState<RelationTreeResponse | null>(null);
  const [crewRolesState, setCrewRolesState] = useState<CrewRole[] | null>(null);
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  const [formValues, setFormValues] = useState<FormValues | null>(null);
  const [selectedFormValues, setSelectedFormValues] = useState<SelectedFormValues | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aircraft, setAircraft] = useState<AircraftDropdownItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce<string>(searchTerm);
  const [filteredRelations, setFilteredRelations] = useState<TreeRole[] | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());
  const [showHiddenCourses, setShowHiddenCourses] = useState(() => {
    const stored = localStorage.getItem("relationsTree.showHiddenCourses");
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [addFormExpanded, setAddFormExpanded] = useState(() => {
    const stored = localStorage.getItem("relationsTree.addFormExpanded");
    return stored !== null ? JSON.parse(stored) : true;
  });
  const { t } = useTranslation();

  useEffect(() => {
    localStorage.setItem("relationsTree.showHiddenCourses", JSON.stringify(showHiddenCourses));
  }, [showHiddenCourses]);

  useEffect(() => {
    localStorage.setItem("relationsTree.addFormExpanded", JSON.stringify(addFormExpanded));
  }, [addFormExpanded]);

  useEffect(() => {
    setSearchTerm("");
  }, [crewId]);

  useEffect(() => {
    setSelectedFormValues(null);
    setCourses([]);
    setError(null);

    (async () => {
      const wantHidden = showHiddenCourses && !isSuperAdmin;
      const [treeJson, coursesJson, roleOptions, crewAircraft] = await Promise.all([
        getRelationTree(crewId, wantHidden),
        getAvailableCourses(crewId ?? 0),
        isSuperAdmin ? getRoles() : getRolesByCrewId(crewId),
        isSuperAdmin ? Promise.resolve(null) : getCrewAircraftTypes({ crewId: crewId as number }),
      ]);

      if (!treeJson) return;

      setVisibleTree(wantHidden ? null : treeJson);
      setMergedTree(wantHidden ? treeJson : null);
      setCrewRolesState(isSuperAdmin ? null : roleOptions);

      const crewTypeIds = crewAircraft ? new Set(crewAircraft.map((a) => a.typeId)) : null;
      const crewDesignatorIds = crewAircraft ? new Set(crewAircraft.map((a) => a.designatorId).filter(Boolean)) : null;

      const designators: AircraftDropdownItem[] = (treeJson.relations.aircraftDesignators || [])
        .filter((a) => !crewDesignatorIds || crewDesignatorIds.has(a.id))
        .map((a) => ({ id: a.id, kind: "designator" as const, name: a.name }));

      const types: AircraftDropdownItem[] = (treeJson.relations.aircraftTypes || [])
        .filter((t) => !crewTypeIds || crewTypeIds.has(t.id))
        .map((type) => ({
          id: type.id,
          kind: "type" as const,
          name: type.name,
          designatorId: type.designatorId ?? null,
        }));

      setAircraft([...designators, ...types]);

      const availableCourses = RelationsService.getCourses(coursesJson) as Course[];
      const formRoles = buildFormRoles(roleOptions, treeJson.relations);

      setFormValues({ roles: formRoles, courses: availableCourses });
      setCoursesLoaded(true);
    })();
  }, [crewId, refresh]);

  const getSelectedNodeIds = () => {
    const ids = new Set<string>();
    const addAircraftExpansionIds = (baseKey: string, aircraftItem: AircraftDropdownItem) => {
      if (aircraftItem.kind === "type" && aircraftItem.designatorId) {
        ids.add(`${baseKey}/a${aircraftItem.designatorId}`);
        ids.add(`${baseKey}/a${aircraftItem.designatorId}/a${aircraftItem.id}`);
        return;
      }
      ids.add(`${baseKey}/a${aircraftItem.id}`);
    };

    selectedFormValues?.roles?.forEach((role) => {
      const roleKey = `/role${role.id}`;
      ids.add(roleKey);

      if (selectedFormValues.position?.roleId === role.id) {
        const positionKey = `${roleKey}/p${selectedFormValues.position.id}`;
        ids.add(positionKey);

        selectedFormValues.operations?.forEach((operation) => {
          ids.add(`${positionKey}/o${operation.id}`);
        });

        selectedFormValues.aircraft?.forEach((aircraftItem) => {
          addAircraftExpansionIds(positionKey, aircraftItem);
          selectedFormValues.operations?.forEach((operation) => {
            addAircraftExpansionIds(`${positionKey}/o${operation.id}`, aircraftItem);
          });
        });
      } else {
        selectedFormValues?.aircraft?.forEach((aircraftItem) => {
          addAircraftExpansionIds(roleKey, aircraftItem);
        });
      }
    });
    return ids;
  };

  useEffect(() => {
    // Wait for the initial load (whichever variant it fetched).
    if (!visibleTree && !mergedTree) return;

    const hasContent = (r: TreeRole) =>
      r.courses?.length || r.customCourses?.length || r.groups?.length || r.positions?.length || r.aircraft?.length;

    const applySearch = (rels: TreeRole[]) => {
      const filtered = filterRelations(
        (crewRolesState ? rels.filter((r) => crewRolesState.some((cr) => cr.id === r.id)) : rels).filter(hasContent),
        debouncedSearchTerm,
      );
      const expandedIds = getExpandedNodeIds(filtered, debouncedSearchTerm);
      getSelectedNodeIds().forEach((id) => expandedIds.add(id));
      setFilteredRelations(filtered);
      setExpandedNodeIds(expandedIds);
    };

    (async () => {
      const wantHidden = showHiddenCourses && !isSuperAdmin;

      let active = wantHidden ? mergedTree : visibleTree;
      if (!active) {
        // The other variant was fetched on load; fetch this one on demand.
        active = await getRelationTree(crewId, wantHidden);
        (wantHidden ? setMergedTree : setVisibleTree)(active);
      }

      const aircraftTypesById = new Map(active.relations.aircraftTypes.map((type) => [type.id, type]));

      // Wire nodes are structurally what the tree renders; group entries are keyed
      // by groupId rather than id, which is why a direct cast does not overlap.
      const treeRoles = active.tree as unknown as TreeRole[];

      applySearch(
        RelationTreeService.sortRelations(filterTreeByFormSelection(treeRoles, selectedFormValues, aircraftTypesById)),
      );
    })();
  }, [courses, debouncedSearchTerm, selectedFormValues, showHiddenCourses, visibleTree, mergedTree]);

  const actions = {
    async toggleCourseVisibility(course: CourseVisibilityToggle) {
      if (!crewId) return;
      const response = await toggleCourseVisibility(crewId, course);
      if (response.ok) setRefresh(refresh + 1);
    },
    async deleteRelation(relation: CourseRelationInput) {
      const response = await deleteRelation(crewId, relation);
      if (response.ok) setRefresh(refresh + 1);
    },
    isSuperAdmin() {
      return isSuperAdmin;
    },
    nodeClicked(nodeId: string) {
      const relation = RelationTreeService.getRelationFromNodeId(nodeId);
      const role = formValues?.roles.find((c) => c.id === relation.roleId);
      if (role) {
        const position = role.positions?.find((p) => p.id === relation.positionId);
        const operation = position?.operations?.find((o) => o.id === relation.operationId);
        const treeAircraftItems: TreeAircraft[] = (position || role)?.aircraft || [];
        const treeAircraft = treeAircraftItems.find((a) => a.id === relation.aircraftTypeId);
        const selectedAircraft = treeAircraft
          ? aircraft.find(
              (a) => a.id === relation.aircraftTypeId && a.kind === (treeAircraft as AircraftDropdownItem).kind,
            )
          : undefined;

        setSelectedFormValues({
          roles: [role],
          position,
          operations: operation ? [operation] : [],
          aircraft: selectedAircraft ? [selectedAircraft] : [],
        });
        formRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    },
  };

  const normalizeAircraftSelection = (selectedAircraft: AircraftDropdownItem[]): AircraftDropdownItem[] => {
    const latestSelection = selectedAircraft.at(-1);
    if (!latestSelection) return [];

    if (latestSelection.kind === "designator") {
      return selectedAircraft.filter(
        (item) =>
          aircraftOptionEquals(item, latestSelection) ||
          item.kind !== "type" ||
          item.designatorId !== latestSelection.id,
      );
    }

    return selectedAircraft.filter(
      (item) =>
        aircraftOptionEquals(item, latestSelection) ||
        item.kind !== "designator" ||
        item.id !== latestSelection.designatorId,
    );
  };

  const aircraftOptionEquals = (left: AircraftDropdownItem, right: AircraftDropdownItem) =>
    left.kind === right.kind && left.id === right.id;

  const onChange = {
    roles(roles: TreeRole[]) {
      const keepDetails = roles.length === 1;
      const nextPosition =
        keepDetails && selectedFormValues?.position?.roleId === roles[0]?.id ? selectedFormValues.position : undefined;
      setSelectedFormValues({
        roles,
        position: nextPosition,
        operations: nextPosition ? selectedFormValues?.operations : [],
        aircraft: keepDetails ? selectedFormValues?.aircraft : [],
      } as SelectedFormValues);
    },
    position(position: SelectedFormValues["position"]) {
      setSelectedFormValues({ roles: selectedFormValues?.roles, position, operations: [] } as SelectedFormValues);
    },
    operations(operations: TreeOperation[]) {
      setSelectedFormValues({
        roles: selectedFormValues?.roles,
        position: selectedFormValues?.position,
        operations,
      } as SelectedFormValues);
    },
    aircraft(selectedAircraft: AircraftDropdownItem[]) {
      setSelectedFormValues({
        roles: selectedFormValues?.roles,
        position: selectedFormValues?.position,
        operations: selectedFormValues?.operations,
        aircraft: normalizeAircraftSelection(selectedAircraft),
      } as SelectedFormValues);
    },
    courses(newCourses: Course[]) {
      setCourses(newCourses);
    },
    clear() {
      setSelectedFormValues({});
      setCourses([]);
      setError(null);
    },
    async submit() {
      if (!courses.length) {
        setError(t("relations_tree.error_specify_course"));
        return;
      }
      if (!selectedFormValues?.roles?.length) {
        setError(t("relations_tree.error_specify_role"));
        return;
      }
      setError(null);
      const selectedAircraft = selectedFormValues.aircraft?.length ? selectedFormValues.aircraft : [undefined];
      const selectedOperations = selectedFormValues.operations?.length ? selectedFormValues.operations : [undefined];
      const relations = selectedFormValues.roles.flatMap((role) =>
        courses.flatMap((course) =>
          selectedOperations.flatMap((operation) =>
            selectedAircraft.map((aircraftItem) => ({
              id: course?.id as number,
              isCustom: course?.isCustom,
              isGroup: course?.isGroup,
              roleId: role.id,
              positionId: selectedFormValues.position?.id,
              operationId: operation?.id,
              designatorId: aircraftItem?.kind === "designator" ? aircraftItem.id : undefined,
              aircraftTypeId: aircraftItem?.kind === "type" ? aircraftItem.id : undefined,
            })),
          ),
        ),
      );
      await Promise.all(relations.map((relation) => createCourseRelation(crewId, relation)));
      setRefresh((prev) => prev + 1);
      setSelectedFormValues({});
      setCourses([]);
    },
  };

  return (
    <CourseRelationsContextProvider actions={actions}>
      <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "background.default" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1, mb: 1 }}>
          <h2>{t("relations_tree.title_short")}</h2>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            <Box sx={{ width: 400 }}>
              <Input
                label={t("search")}
                value={searchTerm}
                onChange={(value: string) => setSearchTerm(value)}
                placeholder={t("relations_tree.search")}
                clearable
              />
            </Box>
            {!isSuperAdmin && (
              <Tooltip
                title={
                  showHiddenCourses ? t("relations_tree.hide_hidden_courses") : t("relations_tree.show_hidden_courses")
                }
              >
                <IconButton onClick={() => setShowHiddenCourses(!showHiddenCourses)} size="medium">
                  {showHiddenCourses ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Tooltip>
            )}
            {coursesLoaded && (
              <Tooltip title={addFormExpanded ? t("relations_tree.collapse_form") : t("relations_tree.expand_form")}>
                <IconButton onClick={() => setAddFormExpanded(!addFormExpanded)} size="medium">
                  {addFormExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {coursesLoaded && (
          <Collapse in={addFormExpanded}>
            <div ref={formRef}>
              <AddRelationsForm
                aircraftList={aircraft}
                selectedValues={selectedFormValues}
                onChange={onChange}
                values={formValues}
                courses={courses}
                error={error}
              />
            </div>
          </Collapse>
        )}
      </Box>
      {filteredRelations && (
        <CourseTree roles={filteredRelations} expandedNodeIds={expandedNodeIds} searchTerm={debouncedSearchTerm} />
      )}
    </CourseRelationsContextProvider>
  );
};

export default RelationsTreeFeature;
